import { api } from "./api";

// resource-cap-enforcement-frontend-changes.md claimed this endpoint serializes PascalCase
// (no camelCase contract resolver). The real response is camelCase — confirmed against the
// actual `GET ResourceCaps/Impact?planId=` output. Trust the wire shape over the doc.
export interface ResourceCapImpact {
  limit: number | null;
  current: number;
  overBy: number;
  wouldDeactivate: number;
}

// A downgrade to a free plan ends every live seat pack separately from the switch itself — the plan
// cap bites today (ResourceCapImpact above), the seats go later, at each pack's own boundary.
export interface SeatLossLine {
  seats: number;
  // The part of `seats` that ends on the next nightly run (PastDue packs whose boundary already
  // passed) — render as "immediately", never as a date.
  seatsEndingImmediately: number;
  // Earliest future boundary, for the other `seats - seatsEndingImmediately` seats. null when there
  // are none.
  endsAt: string | null;
  projectedLimit: number | null;
  projectedOverBy: number;
  projectedWouldDeactivate: number;
}

export interface SeatPacksEnding {
  subscriptionCount: number;
  therapists: SeatLossLine;
  patients: SeatLossLine;
}

export interface ResourceCapsImpactResponse {
  therapists: ResourceCapImpact;
  patients: ResourceCapImpact;
  // null unless the target plan is free AND the company has live packs — never render "you lose 0
  // seats".
  seatPacksEnding: SeatPacksEnding | null;
}

export type ResourceCapsImpactArg =
  | { planId?: number; plansPricesId?: number; paymentsPeriodTypesId?: number }
  | { endingSeatSubscriptionId: number };

export const resourceCapsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getResourceCapsImpact: build.query<ResourceCapsImpactResponse, ResourceCapsImpactArg | void>({
      query: (arg) => {
        if (!arg) return { url: "ResourceCaps/Impact" };

        if ("endingSeatSubscriptionId" in arg) {
          return { url: "ResourceCaps/Impact", params: { endingSeatSubscriptionId: arg.endingSeatSubscriptionId } };
        }

        if (arg.planId === undefined) return { url: "ResourceCaps/Impact" };

        // Never emit one of the pair without the other: fetchBaseQuery builds the querystring with
        // `new URLSearchParams(params)`, which stringifies an `undefined` value as the literal text
        // "undefined" rather than omitting the key, and the backend 400s `invalid_scenario` on a
        // half-given (plansPricesId, paymentsPeriodTypesId) pair. Degrading to planId-alone is safe by
        // construction — the backend documents that shape as exact for a real free plan.
        const hasPriceScenario = arg.plansPricesId != null && arg.paymentsPeriodTypesId != null;

        return {
          url: "ResourceCaps/Impact",
          params: hasPriceScenario
            ? { planId: arg.planId, plansPricesId: arg.plansPricesId, paymentsPeriodTypesId: arg.paymentsPeriodTypesId }
            : { planId: arg.planId },
        };
      },
    }),
  }),
});

export const { useGetResourceCapsImpactQuery } = resourceCapsApi;
